import React, { useState, useEffect, useRef } from 'react';
import {
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Box, CircularProgress, Alert, Typography, Autocomplete, Divider, Chip,
  List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PreviewIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FinancialPreview from './FinancialPreview';
import AddressAutocomplete from '../AddressAutocomplete';
import { validateField } from '../../utils/field-validators';
import billingApi from '../../services/billingApi';
import DowngradeConfirmDialog from '../DowngradeConfirmDialog';
import {
  isManualReview,
  MANUAL_REVIEW_CUSTOMER_TITLE,
  MANUAL_REVIEW_CUSTOMER_DETAIL,
} from '../../utils/workflowStatus';

// US states for the 'state' field type
const US_STATES_OPTIONS = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// ─── User Search with Auth-Service Data ──────────────────────────

function UserSearchField({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  // Load selected user details if value is set but we don't have the user object
  useEffect(() => {
    if (value && !selected) {
      billingApi.get(`/api/admin/workflows/users/search?q=${encodeURIComponent(value)}`)
        .then(data => {
          const match = (data.users || []).find(u => u.id === value);
          if (match) setSelected(match);
        })
        .catch(() => {});
    }
  }, [value]);

  function handleInputChange(_, newValue) {
    setInputValue(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!newValue || newValue.length < 2) {
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await billingApi.get(`/api/admin/workflows/users/search?q=${encodeURIComponent(newValue)}`);
        setOptions(data.users || []);
      } catch (err) {
        console.warn('User search failed:', err.message);
      } finally {
        setLoading(false);
      }
    }, 300);
  }

  return (
    <Autocomplete
      options={options}
      getOptionLabel={u => u.name ? `${u.name} (${u.email})` : u.email}
      value={selected}
      onChange={(_, u) => { setSelected(u); onChange(u?.id || ''); }}
      onInputChange={handleInputChange}
      loading={loading}
      filterOptions={x => x}
      renderInput={params => (
        <TextField
          {...params}
          label="User *"
          size="small"
          placeholder="Search by name, email, or user ID..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, u) => (
        <li {...props} key={u.id}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {u.name || u.username || 'No name'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {u.role === 'admin' && <Chip label="Admin" size="small" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />}
                {u.has_billing_account && <Chip label="Billing" size="small" color="info" sx={{ height: 18, fontSize: '0.6rem' }} />}
                {u.active_subscriptions > 0 && <Chip label={`${u.active_subscriptions} sub${u.active_subscriptions > 1 ? 's' : ''}`} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">{u.email}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.65rem' }}>
              {u.id}
            </Typography>
          </Box>
        </li>
      )}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters...' : 'No users found'}
      size="small"
      fullWidth
    />
  );
}

function formatCents(cents) {
  if (cents == null) return '';
  const negative = cents < 0;
  return `${negative ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

// ─── Smart Field ──────────────────────────────────────────────────

function SmartField({ fieldKey, prop, required, value, onChange, lookups, inputData, mode }) {
  const { products = [], customers = [], subscriptions = [], discounts = [], bundles = [] } = lookups;

  // In customer mode, filter subscriptions to only the current user's
  const filteredSubs = mode === 'customer' && inputData.user_id
    ? subscriptions.filter(s => s.user_id === inputData.user_id)
    : subscriptions;

  if (fieldKey === 'user_id') {
    return <UserSearchField value={value} onChange={onChange} />;
  }

  if (fieldKey === 'productId' || fieldKey === 'product_id') {
    return (
      <FormControl fullWidth size="small">
        <InputLabel>Product {required ? '*' : ''}</InputLabel>
        <Select value={value || ''} label={`Product ${required ? '*' : ''}`} onChange={e => onChange(e.target.value)}>
          {products.map(p => (
            <MenuItem key={p.productId} value={p.productId}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {p.name}
                <Typography variant="caption" color="text.secondary">({p.productId})</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (fieldKey === 'planId' || fieldKey === 'target_planId' || fieldKey === 'plan_id' || fieldKey === 'target_plan_id') {
    const selectedProduct = products.find(p => p.productId === inputData.productId);
    const sub = filteredSubs.find(s => s.id === inputData.subscription_id);
    const subProduct = sub ? products.find(p => p.productId === sub.productId) : null;
    const product = selectedProduct || subProduct;
    const availablePlans = product?.plans || products.flatMap(p => p.plans);
    const label = (fieldKey === 'target_planId' || fieldKey === 'target_plan_id') ? 'Target Plan' : 'Plan';

    return (
      <FormControl fullWidth size="small">
        <InputLabel>{label} {required ? '*' : ''}</InputLabel>
        <Select value={value || ''} label={`${label} ${required ? '*' : ''}`} onChange={e => onChange(e.target.value)}>
          {availablePlans.map(p => (
            <MenuItem key={p.planId} value={p.planId}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>{p.name}</span>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {p.price_cents > 0 ? `${formatCents(p.price_cents)}/${p.interval_type}` : 'Free'}
                  {p.trial_days > 0 ? ` · ${p.trial_days}d trial` : ''}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (fieldKey === 'subscription_id') {
    // In customer mode with one sub, auto-select
    if (mode === 'customer' && filteredSubs.length === 1 && !value) {
      setTimeout(() => onChange(filteredSubs[0].id), 0);
    }
    // Customer mode with one sub — show as read-only card
    if (mode === 'customer' && filteredSubs.length === 1) {
      const s = filteredSubs[0];
      return (
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.plan_name} — {s.product_name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {s.status === 'active' ? 'Active' : s.status} subscription
          </Typography>
        </Box>
      );
    }

    return (
      <Autocomplete
        options={filteredSubs}
        getOptionLabel={s => `${s.email} — ${s.plan_name} (${s.product_name})`}
        value={filteredSubs.find(s => s.id === value) || null}
        onChange={(_, s) => onChange(s?.id || '')}
        renderInput={params => (
          <TextField {...params} label="Subscription *" size="small" placeholder="Search by email or plan..." />
        )}
        renderOption={(props, s) => (
          <li {...props} key={s.id}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{s.email}</Typography>
                <Chip label={s.status} size="small" sx={{ height: 18, fontSize: '0.65rem' }}
                  color={s.status === 'active' ? 'success' : s.status === 'trialing' ? 'info' : 'default'} />
              </Box>
              <Typography variant="caption" color="text.secondary">{s.plan_name} · {s.product_name}</Typography>
            </Box>
          </li>
        )}
        filterOptions={(options, { inputValue }) => {
          const q = inputValue.toLowerCase();
          return options.filter(s => s.email.toLowerCase().includes(q) || s.plan_name.toLowerCase().includes(q) || s.product_name.toLowerCase().includes(q));
        }}
        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
        size="small"
        fullWidth
      />
    );
  }

  if (fieldKey === 'discount_id' || fieldKey === 'discount_code') {
    return (
      <FormControl fullWidth size="small">
        <InputLabel>Discount {required ? '*' : ''}</InputLabel>
        <Select value={value || ''} label={`Discount ${required ? '*' : ''}`} onChange={e => onChange(e.target.value)}>
          {discounts.map(d => (
            <MenuItem key={d.id} value={fieldKey === 'discount_id' ? d.id : d.code}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{d.code}</span>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {d.discount_type === 'percentage' ? `${d.discount_value}% off` :
                   d.discount_type === 'fixed_amount' ? `${formatCents(d.discount_value)} off` :
                   d.discount_type === 'free_period' ? `${d.discount_value} month(s) free` : d.discount_type}
                  {' · '}{d.duration}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (fieldKey === 'bundle_id') {
    return (
      <FormControl fullWidth size="small">
        <InputLabel>Bundle {required ? '*' : ''}</InputLabel>
        <Select value={value || ''} label={`Bundle ${required ? '*' : ''}`} onChange={e => onChange(e.target.value)}>
          {bundles.map(b => (
            <MenuItem key={b.id} value={b.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{b.name}</span>
                <Typography variant="caption" color="text.secondary">{formatCents(b.price_cents)}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (fieldKey === 'reason_code') return null;

  if (fieldKey === 'compensation_type') {
    return (
      <FormControl fullWidth size="small">
        <InputLabel>Compensation</InputLabel>
        <Select value={value || 'none'} label="Compensation" onChange={e => onChange(e.target.value)}>
          <MenuItem value="refund">Refund to payment method</MenuItem>
          <MenuItem value="credit">Account credit</MenuItem>
          <MenuItem value="none">No compensation</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (prop?.type === 'boolean') {
    const labels = {
      cancel_immediately: { label: 'Cancel Timing', yes: 'Cancel immediately', no: 'Cancel at end of billing period' },
      apply_immediately: { label: 'Apply Timing', yes: 'Apply immediately (with credit)', no: 'Apply at end of billing period' },
    };
    const meta = labels[fieldKey] || { label: fieldKey.replace(/_/g, ' '), yes: 'Yes', no: 'No' };
    return (
      <FormControl fullWidth size="small">
        <InputLabel>{meta.label}</InputLabel>
        <Select value={value != null ? String(value) : 'false'} label={meta.label} onChange={e => onChange(e.target.value === 'true')}>
          <MenuItem value="true">{meta.yes}</MenuItem>
          <MenuItem value="false">{meta.no}</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (fieldKey === 'expires_at' || fieldKey === 'resume_at') {
    const label = fieldKey === 'expires_at' ? 'Expires At' : 'Resume At';
    return (
      <TextField label={label} type="datetime-local" value={value ? value.substring(0, 16) : ''}
        onChange={e => onChange(e.target.value ? new Date(e.target.value).toISOString() : '')}
        size="small" fullWidth InputLabelProps={{ shrink: true }}
        helperText={fieldKey === 'expires_at' ? 'Leave empty for indefinite' : undefined} />
    );
  }

  if (prop?.type === 'number' || fieldKey === 'extra_days') {
    const label = fieldKey === 'extra_days' ? 'Additional Days'
      : fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return (
      <TextField label={`${label} ${required ? '*' : ''}`} type="number" value={value || ''}
        onChange={e => onChange(parseInt(e.target.value) || '')} size="small" fullWidth helperText={prop?.description} />
    );
  }

  if (fieldKey === 'email') return null;

  const label = fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <TextField label={`${label} ${required ? '*' : ''}`} value={value || ''}
      onChange={e => onChange(e.target.value)} size="small" fullWidth required={required}
      helperText={prop?.description} multiline={fieldKey === 'reason_text'} rows={fieldKey === 'reason_text' ? 2 : 1} />
  );
}

// ─── Step Progress ────────────────────────────────────────────────

function StepProgress({ steps, stepLogs }) {
  if (!steps || steps.length === 0) return null;

  const logMap = {};
  for (const l of (stepLogs || [])) logMap[l.step_id] = l;

  const statusIcon = (status) => {
    if (status === 'completed') return <CheckCircleIcon fontSize="small" color="success" />;
    if (status === 'failed' || status === 'revert_failed') return <ErrorIcon fontSize="small" color="error" />;
    if (status === 'reverted') return <ErrorIcon fontSize="small" color="warning" />;
    // 999.4737 (harrison review, item 7): 'reverting' is a step_log status
    // introduced by the revert re-entry guard (a step actively being
    // compensated, claimed but not yet resolved) — pre-fix this fell
    // through to the SAME icon as a step that never ran at all, which is
    // actively misleading for money-path visibility.
    if (status === 'reverting') return <HourglassEmptyIcon fontSize="small" color="warning" />;
    if (status === 'running') return <HourglassEmptyIcon fontSize="small" color="info" />;
    return <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
  };

  return (
    <Box>
      <Typography variant="overline" sx={{ fontSize: '0.65rem', color: 'text.secondary', letterSpacing: '0.1em' }}>
        Execution Progress
      </Typography>
      <List dense disablePadding>
        {steps.map(step => {
          const log = logMap[step.id];
          const duration = log?.started_at && log?.completed_at
            ? `${((new Date(log.completed_at) - new Date(log.started_at)) / 1000).toFixed(1)}s`
            : log?.status === 'running' ? '...' : '';

          return (
            <ListItem key={step.id} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>{statusIcon(log?.status)}</ListItemIcon>
              <ListItemText
                primary={step.label || step.id}
                secondary={log?.error_message}
                primaryTypographyProps={{ fontSize: '0.8rem', color: log?.status === 'failed' ? 'error.main' : 'text.primary' }}
                secondaryTypographyProps={{ fontSize: '0.7rem', color: 'error.main' }}
              />
              {duration && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.7rem' }}>
                  {duration}
                </Typography>
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

// ─── Field Ordering ───────────────────────────────────────────────

const FIELD_PRIORITY = {
  subscription_id: 0, user_id: 1, productId: 2, product_id: 2, planId: 3, plan_id: 3, target_planId: 4, target_plan_id: 4,
  bundle_id: 5, discount_id: 6, discount_code: 6, compensation_type: 7,
  cancel_immediately: 8, apply_immediately: 8, extra_days: 8,
  expires_at: 9, resume_at: 9, reason_code: 10, reason_text: 11, email: 12,
};

function orderFields(properties, required) {
  return Object.keys(properties).sort((a, b) => {
    const aReq = required.includes(a) ? 0 : 1;
    const bReq = required.includes(b) ? 0 : 1;
    if (aReq !== bReq) return aReq - bReq;
    return (FIELD_PRIORITY[a] ?? 6) - (FIELD_PRIORITY[b] ?? 6);
  });
}

// ─── Main Form Component ─────────────────────────────────────────

/**
 * Reusable workflow run form.
 *
 * @param {object}   definition    - Workflow definition object
 * @param {string}   mode          - "admin" or "customer"
 * @param {object}   prefilledData - Fields to pre-fill and hide (e.g. { user_id, email })
 * @param {function} onExecuted    - Called with the instance after execution starts
 * @param {boolean}  showProgress  - Show live step progress after execution
 */
export default function WorkflowRunForm({ definition, mode = 'admin', prefilledData = {}, onExecuted, showProgress = true }) {
  const [inputData, setInputData] = useState({});
  const [lookups, setLookups] = useState({});
  const [reasonCodes, setReasonCodes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [error, setError] = useState('');

  // Execution progress state
  const [executedInstance, setExecutedInstance] = useState(null);
  const [stepLogs, setStepLogs] = useState([]);
  const pollRef = useRef(null);

  // Input collection state (for paused_for_input)
  const [inputRequest, setInputRequest] = useState(null);
  const [inputSubmitting, setInputSubmitting] = useState(false);

  // Downgrade confirm dialog state — intercepts execute for downgrade_subscription
  const [downgradeConfirmOpen, setDowngradeConfirmOpen] = useState(false);

  useEffect(() => {
    setInputData({ ...prefilledData });
    setPreview(null);
    setError('');
    setExecutedInstance(null);
    setStepLogs([]);
    loadLookups();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [definition?.workflowId]);

  async function loadLookups() {
    setLookupsLoading(true);
    try {
      const [lookupData, rcData] = await Promise.all([
        billingApi.get('/api/admin/workflows/lookups'),
        billingApi.get('/api/admin/workflows/reason-codes'),
      ]);
      setLookups(lookupData);
      setReasonCodes(rcData.reason_codes || []);
    } catch (err) {
      console.warn('Failed to load lookups:', err.message);
    } finally {
      setLookupsLoading(false);
    }
  }

  async function handlePreview() {
    if (!definition) return;
    setPreviewLoading(true);
    setError('');
    try {
      const endpoint = mode === 'customer'
        ? `/api/workflows/${definition.workflowId}/preview`
        : '/api/admin/workflows/preview';
      const body = mode === 'customer'
        ? { input_data: { ...prefilledData, ...inputData } }
        : { definition_workflowId: definition.workflowId, input_data: { ...prefilledData, ...inputData } };
      const data = await billingApi.post(endpoint, body);
      setPreview(data.preview);
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleExecute() {
    if (!definition) return;
    setLoading(true);
    setError('');
    try {
      const execEndpoint = mode === 'customer'
        ? `/api/workflows/${definition.workflowId}/execute`
        : '/api/admin/workflows/execute';
      const execBody = mode === 'customer'
        ? { input_data: { ...prefilledData, ...inputData } }
        : { definition_workflowId: definition.workflowId, input_data: { ...prefilledData, ...inputData } };
      const data = await billingApi.post(execEndpoint, execBody);
      const inst = data.instance;
      setExecutedInstance(inst);
      if (onExecuted) onExecuted(inst);

      // Start polling for progress
      if (showProgress && inst.id) {
        pollProgress(inst.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Intercept the execute button for the downgrade_subscription workflow in
   * customer mode: show the DowngradeConfirmDialog (which fetches the
   * downgrade-impact preview) before proceeding. For all other workflows or
   * admin mode, execute directly.
   */
  function handleExecuteClick() {
    const allData = { ...prefilledData, ...inputData };
    const isDowngrade = definition?.workflowId === 'downgrade_subscription'
      && mode === 'customer'
      && allData.subscription_id
      && allData.target_plan_id;

    if (isDowngrade) {
      setDowngradeConfirmOpen(true);
    } else {
      handleExecute();
    }
  }

  function pollProgress(instanceId) {
    async function fetchStatus() {
      try {
        const data = await billingApi.get(`/api/admin/workflows/instances/${instanceId}`);

        // Detect paused_for_input — stop polling and show input form
        if (data.instance?.status === 'paused_for_input' && data.input_requests?.length > 0) {
          const pending = data.input_requests.find(r => r.status === 'pending');
          if (pending) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            // Only set state once — avoid re-renders that reset the form
            setInputRequest(prev => prev?.id === pending.id ? prev : pending);
            setExecutedInstance(data.instance);
            return; // Don't update anything else
          }
        }

        // Detect paused_for_payment — stop polling and show checkout link
        if (data.instance?.status === 'paused_for_payment') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          setExecutedInstance(data.instance);
          return;
        }

        setExecutedInstance(data.instance);
        setStepLogs(data.step_logs || []);

        // 999.4760: 'manual_review' (999.4740) is TERMINAL — there is no
        // supported transition out of it (999.4741) — so it belongs in this
        // stop-polling check same as the resolved outcomes. It is NOT a
        // resolved outcome itself (not success, not failure); the caller
        // (onExecuted) and the render below both give it its own branch.
        // (999.4746 PAY-WORKFLOW-STATUS-CONTRACT: this array is a registered
        // consumer, mechanically checked as a literal at THIS call site —
        // keep it inline, never a shared imported constant, so a human
        // adding a status must walk past every reader that owns a copy.)
        if (['completed', 'failed', 'canceled', 'undone', 'manual_review'].includes(data.instance?.status)) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          // Notify parent of terminal state (for modal close, etc.)
          if (onExecuted) onExecuted(data.instance);
        }
      } catch (err) {
        console.warn('Poll failed:', err.message);
      }
    }
    fetchStatus(); // Immediate first fetch
    pollRef.current = setInterval(fetchStatus, 2000);
  }

  function updateField(key, value) {
    setInputData(prev => ({ ...prev, [key]: value }));
  }

  if (!definition) return null;

  const schema = definition.input_schema || {};
  const properties = schema.properties || {};
  const required = schema.required || [];
  const hiddenFields = definition.hidden_fields || [];
  const steps = definition.steps || [];

  // Determine which fields to show
  const fieldOrder = orderFields(properties, required).filter(key => {
    if (key === 'email') return false;
    if (mode === 'customer') {
      if (hiddenFields.includes(key)) return false;
      if (['user_id', 'reason_code', 'reason_text'].includes(key)) return false;
      if (prefilledData[key] !== undefined) return false;
    }
    if (key === 'reason_code') return false; // Rendered in reason section
    return true;
  });

  const showReasonSection = mode === 'admin' && (properties.reason_code || properties.reason_text);
  const isExecuted = !!executedInstance;
  // (999.4746 PAY-WORKFLOW-STATUS-CONTRACT: registered consumer, checked as
  // its OWN inline literal, independent of the pollProgress copy above — two
  // copies drifting apart is exactly the bug this duplication catches.)
  const isTerminal = ['completed', 'failed', 'canceled', 'undone', 'manual_review'].includes(executedInstance?.status);
  const isPausedForInput = executedInstance?.status === 'paused_for_input' && inputRequest;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Execution result banner */}
      {isExecuted && isTerminal && (
        <Alert
          severity={
            executedInstance.status === 'completed' ? 'success'
              : isManualReview(executedInstance.status) ? 'warning'
              : 'error'
          }
          sx={{ mb: 2 }}
        >
          {executedInstance.status === 'completed'
            ? 'Workflow completed successfully'
            : isManualReview(executedInstance.status)
              ? 'Workflow parked for manual review — a provider action may not be confirmed yet. It will not be retried automatically.'
              : `Workflow ${executedInstance.status}: ${executedInstance.error_message || ''}`}
        </Alert>
      )}

      {/* Input collection form (paused_for_input) */}
      {executedInstance?.status === 'paused_for_input' && inputRequest && (
        <InputCollectionForm
          inputRequest={inputRequest}
          loading={inputSubmitting}
          onSubmit={async (data) => {
            setInputSubmitting(true);
            setError('');
            try {
              const endpoint = mode === 'customer'
                ? `/api/workflows/instances/${executedInstance.id}/submit-input`
                : `/api/admin/workflows/instances/${executedInstance.id}/submit-input`;
              await billingApi.post(endpoint, { data });
              setInputRequest(null);
              // Resume polling to track remaining workflow execution
              pollProgress(executedInstance.id);
            } catch (err) {
              setError(err.message);
            } finally {
              setInputSubmitting(false);
            }
          }}
        />
      )}

      {/* Payment pending (paused_for_payment) */}
      {executedInstance?.status === 'paused_for_payment' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Waiting for payment</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            A checkout session has been created. The workflow will resume automatically when payment is completed.
          </Typography>
          {executedInstance.context?.collect_payment?.checkout_url && (
            <Button
              variant="outlined" size="small"
              href={executedInstance.context.collect_payment.checkout_url}
              target="_blank"
              sx={{ textTransform: 'none' }}
            >
              Open Checkout Page
            </Button>
          )}
        </Alert>
      )}

      {lookupsLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : isPausedForInput || executedInstance?.status === 'paused_for_payment' ? (
        null /* InputCollectionForm or payment pending is rendered above */
      ) : !isExecuted ? (
        <>
          {/* Form fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fieldOrder.map(key => (
              <SmartField
                key={key}
                fieldKey={key}
                prop={properties[key]}
                required={required.includes(key)}
                value={inputData[key]}
                onChange={val => updateField(key, val)}
                lookups={lookups}
                inputData={{ ...prefilledData, ...inputData }}
                mode={mode}
              />
            ))}

            {/* Reason section (admin only) */}
            {showReasonSection && (
              <>
                <Divider sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Reason</Typography>
                </Divider>
                {properties.reason_code && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Reason Code</InputLabel>
                    <Select value={inputData.reason_code || ''} label="Reason Code"
                      onChange={e => updateField('reason_code', e.target.value)}>
                      {reasonCodes.map(rc => (
                        <MenuItem key={rc.code} value={rc.code}>{rc.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {properties.reason_text && (
                  <TextField label="Notes" value={inputData.reason_text || ''}
                    onChange={e => updateField('reason_text', e.target.value)}
                    size="small" fullWidth multiline rows={2} placeholder="Add context for the audit trail..." />
                )}
              </>
            )}
          </Box>

          {/* Preview */}
          {preview && (
            <Box sx={{ mt: 2 }}>
              {preview.steps?.map((step, i) => (
                <Box key={i}>
                  {step.step_type === 'preview' && step.result?.output?.financial_preview && (
                    <FinancialPreview preview={step.result.output.financial_preview} />
                  )}
                </Box>
              ))}
              {!preview.valid && <Alert severity="warning" sx={{ mt: 1 }}>Validation failed — check the fields above</Alert>}
              {preview.valid && <Alert severity="success" sx={{ mt: 1 }}>Validation passed — ready to execute</Alert>}
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={handlePreview} disabled={previewLoading} variant="outlined"
              startIcon={previewLoading ? <CircularProgress size={16} /> : <PreviewIcon />}>
              Preview
            </Button>
            <Button onClick={handleExecuteClick} disabled={loading} variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <PlayArrowIcon />}>
              {mode === 'customer' ? (definition.public_name || definition.name) : 'Execute'}
            </Button>
          </Box>
        </>
      ) : mode === 'customer' ? (
        /* Post-execution: customer-friendly result */
        <CustomerResult instance={executedInstance} definition={definition} />
      ) : (
        /* Post-execution: admin step progress */
        <StepProgress steps={steps} stepLogs={stepLogs} />
      )}

      {/* Downgrade impact confirm dialog — shown before executing downgrade_subscription */}
      <DowngradeConfirmDialog
        open={downgradeConfirmOpen}
        onClose={() => setDowngradeConfirmOpen(false)}
        onConfirm={() => {
          setDowngradeConfirmOpen(false);
          handleExecute();
        }}
        subscriptionId={{ ...prefilledData, ...inputData }.subscription_id}
        targetPlanSlug={{ ...prefilledData, ...inputData }.target_plan_id}
      />
    </Box>
  );
}

// ─── Input Collection Form (for paused_for_input) ─────────────

function InputCollectionForm({ inputRequest, onSubmit, loading: submitting }) {
  const [formData, setFormData] = useState(() => {
    const defaults = {};
    for (const field of (inputRequest.required_fields || [])) {
      if (field.default) defaults[field.key] = field.default;
      // Address composite: default country to US
      if (field.type === 'address') {
        defaults[field.key] = { street: '', city: '', state: '', zip: '', country: 'US' };
      }
    }
    return defaults;
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [addressVerified, setAddressVerified] = useState(false);

  function validateAndSubmit() {
    const errors = {};
    let valid = true;
    for (const field of fields) {
      if (field.type === 'address') {
        // Address composite: check that sub-fields exist
        const addr = formData[field.key];
        if (field.required && (!addr || !addr.street || !addr.state || !addr.city || !addr.zip)) {
          errors[field.key] = 'Complete billing address is required';
          valid = false;
        }
      } else {
        const result = validateField(formData[field.key], field);
        if (!result.valid) {
          errors[field.key] = result.message;
          valid = false;
        }
      }
    }
    setFieldErrors(errors);
    if (valid) onSubmit(formData);
  }

  function handleBlur(field) {
    if (field.type === 'address') return; // Address validates on submit
    const result = validateField(formData[field.key], field);
    setFieldErrors(prev => ({ ...prev, [field.key]: result.valid ? undefined : result.message }));
  }

  function handleAddressSelect(field, addressData) {
    setFormData(prev => ({
      ...prev,
      [field.key]: {
        street: addressData.street_address || '',
        city: addressData.city || '',
        state: addressData.state || '',
        zip: addressData.zip_code || '',
        country: addressData.country || 'US',
      }
    }));
    setAddressVerified(true);
    setFieldErrors(prev => ({ ...prev, [field.key]: undefined }));
  }

  function handleAddressManualChange(field, streetValue) {
    setFormData(prev => ({
      ...prev,
      [field.key]: {
        ...(prev[field.key] || {}),
        street: streetValue,
      }
    }));
    setAddressVerified(false);
  }

  const fields = inputRequest.required_fields || [];

  // Group fields into rows based on render.width
  const rows = [];
  let currentRow = [];
  for (const field of fields) {
    const width = field.render?.width || 'full';
    if (width === 'half') {
      currentRow.push(field);
      if (currentRow.length === 2) {
        rows.push(currentRow);
        currentRow = [];
      }
    } else {
      if (currentRow.length > 0) {
        rows.push(currentRow);
        currentRow = [];
      }
      rows.push([field]);
    }
  }
  if (currentRow.length > 0) rows.push(currentRow);

  function renderField(field) {
    const errMsg = fieldErrors[field.key];
    const render = field.render || {};

    // Address composite type — always show individual fields
    if (field.type === 'address') {
      const addrData = formData[field.key] || {};
      const updateAddr = (subKey, val) => {
        setFormData(prev => ({
          ...prev,
          [field.key]: { ...(prev[field.key] || {}), [subKey]: val }
        }));
        if (fieldErrors[field.key]) setFieldErrors(prev => ({ ...prev, [field.key]: undefined }));
      };
      return (
        <Box key={field.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {errMsg && (
            <Typography variant="caption" color="error">{errMsg}</Typography>
          )}
          <AddressAutocomplete
            value={addrData.street || ''}
            onChange={(val) => updateAddr('street', val)}
            onAddressSelect={(addr) => handleAddressSelect(field, addr)}
            label="Street Address *"
            placeholder={render.placeholder || 'Start typing for suggestions...'}
            error={!!errMsg && !addrData.street}
            required
          />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              size="small" label="City *" value={addrData.city || ''}
              onChange={e => updateAddr('city', e.target.value)}
              error={!!errMsg && !addrData.city}
              sx={{ flex: 2 }}
              required
            />
            <FormControl size="small" sx={{ flex: 1, minWidth: 80 }} error={!!errMsg && !addrData.state}>
              <InputLabel>State *</InputLabel>
              <Select value={addrData.state || ''} label="State *"
                onChange={e => updateAddr('state', e.target.value)}>
                {US_STATES_OPTIONS.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small" label="ZIP *" value={addrData.zip || ''}
              onChange={e => updateAddr('zip', e.target.value)}
              error={!!errMsg && !addrData.zip}
              inputProps={{ maxLength: 10 }}
              sx={{ flex: 1 }}
              required
            />
          </Box>
          <TextField
            size="small" label="Country" value={addrData.country || 'US'}
            onChange={e => updateAddr('country', e.target.value)}
            sx={{ maxWidth: 120 }}
            inputProps={{ maxLength: 2 }}
            helperText="2-letter code"
          />
          {addressVerified && (
            <Typography variant="caption" color="success.main" sx={{ pl: 0.5 }}>
              Address verified via Google
            </Typography>
          )}
        </Box>
      );
    }

    // Select type
    if (field.type === 'select' || field.type === 'state') {
      const options = field.type === 'state' ? US_STATES_OPTIONS : (field.options || []);
      return (
        <FormControl key={field.key} fullWidth size="small" error={!!errMsg}>
          <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
          <Select
            value={formData[field.key] || ''}
            label={`${field.label}${field.required ? ' *' : ''}`}
            onChange={e => {
              setFormData(prev => ({ ...prev, [field.key]: e.target.value }));
              setFieldErrors(prev => ({ ...prev, [field.key]: undefined }));
            }}
          >
            {options.map(opt => (
              <MenuItem key={opt.value || opt} value={opt.value || opt}>
                {opt.label || opt}
              </MenuItem>
            ))}
          </Select>
          {errMsg && <Typography variant="caption" color="error" sx={{ mt: 0.5, pl: 1.5 }}>{errMsg}</Typography>}
        </FormControl>
      );
    }

    // Text / email / phone types
    return (
      <TextField
        key={field.key}
        label={`${field.label}${field.required ? ' *' : ''}`}
        value={formData[field.key] || ''}
        onChange={e => {
          setFormData(prev => ({ ...prev, [field.key]: e.target.value }));
          if (fieldErrors[field.key]) setFieldErrors(prev => ({ ...prev, [field.key]: undefined }));
        }}
        onBlur={() => handleBlur(field)}
        size="small"
        fullWidth
        required={field.required}
        error={!!errMsg}
        helperText={errMsg || undefined}
        placeholder={render.placeholder}
        inputProps={{
          maxLength: render.maxLength || field.validation?.maxLength,
          inputMode: render.inputMode,
          autoComplete: render.autoComplete || 'off',
        }}
        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
      />
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {inputRequest.prompt_message || 'Additional information is required to continue.'}
      </Alert>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((row, i) => (
          row.length === 1 ? (
            <Box key={i}>{renderField(row[0])}</Box>
          ) : (
            <Box key={i} sx={{ display: 'flex', gap: 2 }}>
              {row.map(field => (
                <Box key={field.key} sx={{ flex: 1 }}>{renderField(field)}</Box>
              ))}
            </Box>
          )
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          onClick={validateAndSubmit}
          disabled={submitting}
          variant="contained"
          sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}
        >
          {submitting ? <CircularProgress size={16} color="inherit" /> : 'Continue'}
        </Button>
      </Box>
    </Box>
  );
}

// ─── Customer-Friendly Result ─────────────────────────────────

const RESULT_MESSAGES = {
  cancel_at_period_end: {
    success: 'Your subscription has been canceled',
    detail: 'You\'ll continue to have access until the end of your current billing period. No further charges will be made.',
  },
  cancel_subscription: {
    success: 'Your subscription has been canceled',
    detail: 'Your access has been ended immediately.',
  },
  upgrade_with_charge: {
    success: 'Your plan has been upgraded',
    detail: 'Your new plan is now active. Any prorated charges will appear on your next invoice.',
  },
  downgrade_subscription: {
    success: 'Your plan change is scheduled',
    detail: 'Your plan will change at the end of your current billing period.',
  },
  pause_subscription: {
    success: 'Your subscription has been paused',
    detail: 'Billing is suspended. You can resume anytime from your subscriptions page.',
  },
  resume_subscription: {
    success: 'Your subscription has been resumed',
    detail: 'Billing has restarted and you have full access again.',
  },
  enroll_free: {
    success: 'You\'re all set',
    detail: 'Your free plan is now active.',
  },
  enroll_subscription: {
    success: 'Your subscription is active',
    detail: 'You now have full access to all features.',
  },
};

function CustomerResult({ instance, definition }) {
  const workflowId = definition?.workflowId;
  const isFailed = instance?.status === 'failed';
  const isRunning = instance?.status === 'running';
  const isPaused = instance?.status?.startsWith('paused_');
  const isParkedForReview = isManualReview(instance?.status);
  // 999.4741/999.4758/999.4760 BLOCK 4 (harrison review): this used to be a
  // DENYLIST — only running/paused/failed/parked were excluded from the
  // green checkmark below, so 'canceled', 'undone', and any absent/
  // unrecognized status all fell through to an unconditional success. Only
  // 'completed' is a KNOWN, verified success — invert to an ALLOWLIST so
  // nothing new (or 'canceled'/'undone', both real engine-level admin
  // actions, not a business-success outcome) can silently render "Done!"
  // again.
  const isSuccess = instance?.status === 'completed';

  const msg = RESULT_MESSAGES[workflowId] || {
    success: 'Done!',
    detail: 'Your request has been processed.',
  };

  if (isRunning || isPaused) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CircularProgress size={36} sx={{ mb: 2 }} />
        <Typography variant="body1">Processing your request...</Typography>
      </Box>
    );
  }

  if (isFailed) {
    return (
      <Alert severity="error">
        <Typography variant="body2" fontWeight={600}>Something went wrong</Typography>
        <Typography variant="caption">
          {instance.error_message || 'Please try again or contact support.'}
        </Typography>
      </Alert>
    );
  }

  // 999.4760: manual_review is neither success nor failure — a provider
  // action may already have executed and is unverified (999.4741). It must
  // NEVER fall through to the default success branch below.
  if (isParkedForReview) {
    return (
      <Alert severity="warning">
        <Typography variant="body2" fontWeight={600}>{MANUAL_REVIEW_CUSTOMER_TITLE}</Typography>
        <Typography variant="caption">{MANUAL_REVIEW_CUSTOMER_DETAIL}</Typography>
      </Alert>
    );
  }

  if (isSuccess) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" sx={{ mb: 0.5 }}>{msg.success}</Typography>
        <Typography variant="body2" color="text.secondary">{msg.detail}</Typography>
      </Box>
    );
  }

  // Anything else reaching here (canceled, undone, or an unrecognized/absent
  // status) is NOT a verified success — say so plainly instead of guessing
  // either way.
  return (
    <Alert severity="info">
      <Typography variant="body2" fontWeight={600}>We don't have a final result to show yet</Typography>
      <Typography variant="caption">
        Check your account or contact support if this doesn't update soon.
      </Typography>
    </Alert>
  );
}

export { SmartField, StepProgress, CustomerResult, orderFields };
